import {useFlags} from 'launchdarkly-react-client-sdk';
import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Breadcrumbs, Link as MuiLink, Typography} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {useSelector} from 'react-redux';
import parse from 'html-react-parser';
import {
  selectEffectiveOrg,
  selectIsExternalToEffectiveOrg,
} from '../../data/session';
import {useGetFolderQuery} from '../../data/documentWorkspaceApi';
import {getSharedDocumentData} from '../../util/sharedDocumentStorage';
import TruncatedTooltip from '../common/TruncatedTooltip';
import {WHITE_100, WHITE_80} from '../../theme';
import {useNavigationGuard} from '../../context/NavigationGuardContext';

const separatorIconSx = {color: WHITE_100, width: 12, height: 12};

const deslugify = (slug) => {
  return slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const NavBreadcrumbs = React.memo(() => {
  const flags = useFlags();
  const location = useLocation();
  const {pathname} = location;
  const {isAuthenticated, isShareLinkMode, isExternalUser} = useSelector(
    (state) => state.session
  );
  const effectiveOrg = useSelector(selectEffectiveOrg);
  const isExternalToEffectiveOrg = useSelector(selectIsExternalToEffectiveOrg);

  const {document: documentStorage} = useSelector((state) => state.documents);
  const documentFoldersEnabled = flags.documentFolders;

  const sharedDocumentData = isShareLinkMode ? getSharedDocumentData() : null;
  const shareLinkOrgIdMatches =
    isShareLinkMode && sharedDocumentData?.orgId === effectiveOrg?.orgId;

  const {data: folderData, isLoading: folderDataLoading} = useGetFolderQuery(
    {folderId: documentStorage.folderId},
    {
      skip:
        !documentFoldersEnabled ||
        !documentStorage.folderId ||
        !isAuthenticated ||
        !effectiveOrg.orgId ||
        isExternalToEffectiveOrg ||
        (isShareLinkMode && !shareLinkOrgIdMatches),
    }
  );

  const navigate = useNavigate();
  const {runGuarded} = useNavigationGuard();

  const _getFilename = () => {
    if (documentStorage?.name) {
      return parse(documentStorage.name);
    }
    return '';
  };

  const isDisclosureLibrary = pathname.startsWith('/disclosure-library/');
  const isRollForwardWalkThrough = pathname.startsWith(
    '/rollforward-walk-through/'
  );
  const shouldWaitForFolderData =
    documentFoldersEnabled &&
    !isDisclosureLibrary &&
    !isRollForwardWalkThrough &&
    !isExternalToEffectiveOrg &&
    !(isShareLinkMode && !shareLinkOrgIdMatches);

  // Hide breadcrumbs in share link mode if the shared document org doesnt match the current org
  if (isShareLinkMode && !shareLinkOrgIdMatches) {
    return null;
  }

  if (shouldWaitForFolderData && (!folderData || folderDataLoading)) {
    return null;
  }

  const getBreadcrumbData = () => {
    if (isDisclosureLibrary) {
      return {path: '/disclosure-library', text: 'Disclosure library'};
    }

    if (isExternalUser) {
      return {
        path: `/shared/org/${effectiveOrg?.orgId}`,
        text: effectiveOrg?.orgName || 'Documents',
      };
    }

    if (isRollForwardWalkThrough) {
      const path =
        documentFoldersEnabled && folderData
          ? `/folders/${folderData.id}/documents`
          : '/documents';
      return {path, text: 'Reports'};
    }

    if (documentFoldersEnabled && folderData) {
      return {
        path: `/folders/${folderData.id}/documents`,
        text: `${folderData.year} - ${folderData.name}`,
      };
    }

    return {path: '/documents', text: 'Reports'};
  };

  const {path: backPath, text: breadcrumbText} = getBreadcrumbData();

  return (
    <Breadcrumbs
      separator={<ChevronRightIcon sx={separatorIconSx} />}
      aria-label="breadcrumb"
      sx={{flexWrap: 'nowrap', '& .MuiBreadcrumbs-ol': {flexWrap: 'nowrap'}}}
    >
      {/* 
          Show breadcrumb for:
          - External users (no orgs) → links to /shared/org/:orgId
          - Regular internal users → links to documents/folders
          Hide for internal users viewing a shared doc from another org
        */}
      {(isExternalUser || !isExternalToEffectiveOrg) && (
        <TruncatedTooltip title={breadcrumbText}>
          <MuiLink
            component="a"
            href={backPath}
            onClick={(e) => {
              // Only intercept unmodified primary-button clicks; let the browser
              // handle modifier/middle-clicks and other native link behaviors.
              if (
                e.button === 0 &&
                !e.metaKey &&
                !e.ctrlKey &&
                !e.shiftKey &&
                !e.altKey
              ) {
                e.preventDefault();
                runGuarded(() => navigate(backPath));
              }
            }}
            color={WHITE_80}
            sx={{
              textDecoration: 'none',
              maxWidth: 'clamp(100px, 20vw, 500px)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              fontSize: '0.9375rem',
              fontWeight: 500,
            }}
          >
            {breadcrumbText}
          </MuiLink>
        </TruncatedTooltip>
      )}
      {(() => {
        const filenameText = isDisclosureLibrary
          ? deslugify(pathname.split('/').pop())
          : _getFilename();
        return (
          <TruncatedTooltip
            title={
              isDisclosureLibrary ? filenameText : (documentStorage?.name ?? '')
            }
          >
            <Typography
              color={WHITE_100}
              noWrap
              sx={{
                maxWidth: 'clamp(150px, 40vw, 1200px)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '0.9375rem',
                fontWeight: 600,
              }}
            >
              {filenameText}
            </Typography>
          </TruncatedTooltip>
        );
      })()}
    </Breadcrumbs>
  );
});

NavBreadcrumbs.displayName = 'NavBreadcrumbs';
